import { useCallback, useState } from "react";
import type { z } from "zod";
import { type FieldErrors, NO_ERRORS, validateWith } from "./validate";

/**
 * The form half every editor shares (plan D09): a local draft built from the
 * server value, validated with the contract's Zod schema on submit, and
 * reported as a parsed payload. No form library.
 *
 * ── THE DRAFT RESETS WHEN THE SERVER VALUE CHANGES ──────────────────────────
 * Compared by identity, during render (React's "adjust state when a prop
 * changes" pattern, no effect): Query's structural sharing keeps the same
 * object when a refetch brings back equal data, so a background refetch never
 * wipes what the person is typing, and a real change always replaces it.
 *
 * ── A REFUSAL IS A FIELD ERROR ─────────────────────────────────────────────
 * `precheck` names unanswered questions before the schema runs; the schema's
 * own refinements come back through `validateWith` as `errors`. `submit`
 * returns whether `onSubmit` was called, and never throws for bad input.
 *
 * The form bodies (`*-form.tsx`) stay pure views over `draft` / `setDraft`,
 * so the onboarding stepper (T52) can drive the same controls from its own
 * state.
 */
export interface EditorFormOptions<S, D, P, Sch extends z.ZodType> {
  onSubmit: (payload: z.output<Sch>) => void;
  precheck?: (draft: D) => FieldErrors;
  schema: Sch;
  server: S;
  toDraft: (server: S) => D;
  toPayload: (draft: D) => P;
}

export function useEditorForm<S, D, P, Sch extends z.ZodType>(
  options: EditorFormOptions<S, D, P, Sch>
) {
  const { server, toDraft, toPayload, schema, precheck, onSubmit } = options;
  const [seen, setSeen] = useState<S>(server);
  const [draft, setDraftState] = useState<D>(() => toDraft(server));
  const [errors, setErrors] = useState<FieldErrors>(NO_ERRORS);

  if (!Object.is(seen, server)) {
    setSeen(server);
    setDraftState(toDraft(server));
    setErrors(NO_ERRORS);
  }

  const setDraft = useCallback((next: D) => {
    setDraftState(next);
    setErrors(NO_ERRORS);
  }, []);

  const submit = useCallback((): boolean => {
    const early = precheck?.(draft) ?? NO_ERRORS;
    if (Object.keys(early).length > 0) {
      setErrors(early);
      return false;
    }
    const result = validateWith(schema, toPayload(draft));
    if (!result.ok) {
      setErrors(result.errors);
      return false;
    }
    onSubmit(result.value);
    return true;
  }, [draft, precheck, schema, toPayload, onSubmit]);

  return { draft, errors, setDraft, setErrors, submit };
}
